import { Tooltip, Typography } from '@mui/material';
import { BenchmarkGrid } from '@strength/domain';
import { WorkoutExerciseMetric } from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { MdBarChart } from '@teamworksdev/icons';
import { CustomSlot } from '../../../components/sheet/slots';
import { cn } from '../../../components/utils';
import { getBenchmarkBadgeGridName } from '../../../hooks/useBenchmarks';
import { ActualSlot } from './types';

const rowHeaderStyle = 'bg-gray-100 p-1';

export const getMetricHeaderSlots = (
  metrics: WorkoutExerciseMetric[],
  x: number,
  y: number,
  t: I18nTranslator,
  getBenchmarkGrid: (benchmarkGridId: string) => BenchmarkGrid | null,
  defaultBenchmarkGridId: string,
  libraryExerciseId?: string,
  hasPercentageIntensity?: boolean,
): ActualSlot[] => {
  let curX = x;

  const athleteNameLabelSlot: ActualSlot = new CustomSlot(
    { x: curX, y },
    1,
    1,
    false,
    () => (
      <Typography variant="subtitle1Semibold" className="text-black/80">
        {t('athlete')}
      </Typography>
    ),
    { className: cn(rowHeaderStyle, 'sticky left-0 w-full w-[180px]') },
  );

  curX += 1;

  const setHeaderSlot: ActualSlot = new CustomSlot(
    { x: curX, y },
    1,
    1,
    false,
    () => (
      <Typography variant="subtitle1Semibold" className="text-black/80">
        {t('set')}
      </Typography>
    ),
    {
      className: cn(rowHeaderStyle, 'sticky left-[180px] w-full w-[80px]'),
    },
  );
  curX++;

  // We generate duplicate metrics for prescribed vs actual
  const metricsSlots = [...metrics, ...metrics].map((metric) => {
    return getMetricHeaderSlot(
      metric,
      curX++,
      y,
      getBenchmarkGrid,
      defaultBenchmarkGridId,
      t,
      libraryExerciseId,
      hasPercentageIntensity,
    );
  });

  // Add single flexible blank metric header
  const baseUsedColumns = 2 + metrics.length * 2; // athlete + set + (prescribed + actual metrics)

  metricsSlots.push(
    new CustomSlot({ x: baseUsedColumns, y }, 1, 1, true, () => <></>, {
      className: 'bg-transparent pointer-events-none min-w-0',
    }),
  );

  return [athleteNameLabelSlot, setHeaderSlot, ...metricsSlots];
};

const getMetricHeaderSlot = (
  metric: WorkoutExerciseMetric,
  x: number,
  y: number,
  getBenchmarkGrid: (benchmarkGridId: string) => BenchmarkGrid | null,
  defaultBenchmarkGridId: string,
  t: I18nTranslator,
  libraryExerciseId?: string,
  hasPercentageIntensity?: boolean,
): ActualSlot => {
  const benchmarkGridName = getBenchmarkBadgeGridName(
    metric,
    getBenchmarkGrid,
    defaultBenchmarkGridId,
    { libraryExerciseId, hasPercentageIntensity },
  );

  return new CustomSlot(
    { x, y },
    1,
    1,
    false,
    () => (
      <div className="flex items-center justify-between w-full">
        <Typography
          className={cn(rowHeaderStyle, 'text-black/80')}
          key={metric.metricId}
          variant="subtitle1Semibold"
        >
          {metric.metricName}
        </Typography>
        {benchmarkGridName && (
          <Tooltip
            title={
              <div>
                <div className="text-white font-semibold mb-1">
                  {t('benchmark')}
                </div>
                <div className="text-gray-100">{benchmarkGridName}</div>
              </div>
            }
          >
            <div className="mt-0.5">
              <MdBarChart />
            </div>
          </Tooltip>
        )}
      </div>
    ),
    { className: rowHeaderStyle },
  );
};
